import type {
    GetWasteContainerRequestsReportData,
    WasteContainerRequestContoareDto,
    WasteContainerRequestDto,
    WasteContainerRequestEditDto,
    WasteContainerRequestReportDto,
} from '@/api/generated/types.gen';
import {
    createWasteContainerRequest,
    editWasteContainerRequest,
    getOwnWasteContainerRequests,
    getWasteContainerRequestsReport,
    getWasteContainerRequestsReportCounters,
    markWasteContainerRequestDelivered,
    markWasteContainerRequestProcessed,
} from '@/api/generated/sdk.gen';
import { handleResponse } from '@/core/api/response-handler';
import { normalizePage } from '@/core/api/spring-types';
import type { Page, Pageable } from '@/core/api/spring-types';

export type {
    WasteContainerRequestContoareDto,
    WasteContainerRequestDto,
    WasteContainerRequestEditDto,
    WasteContainerRequestReportDto,
};

/** Filtrele raportului de administrare (`editionId`, `stareRand`, `fel`, `cautare`), din spec. */
export type FiltruRaportRecipiente = Omit<GetWasteContainerRequestsReportData['query'], 'pageable'>;

/** Cele doua feluri, exact cum le numeste enumul din backend. */
export type FelRecipient = NonNullable<WasteContainerRequestDto['fel']>;
/** `NOUA` = nimeni n-a atins-o inca · `PROCESATA` = un OM s-a ocupat de ea. */
export type StareCerere = NonNullable<WasteContainerRequestDto['stare']>;

/**
 * Cererile de recipiente (SP-04) — invelis subtire peste SDK-ul generat.
 *
 * 🔴 FIECARE apel trece prin `handleResponse(...)`. SDK-ul generat are
 * `ThrowOnError = false`, deci INTOARCE eroarea in loc s-o arunce: un
 * `await someSdkFn(...)` fara `handleResponse` merge mai departe ca si cum ar fi
 * reusit, chiar la 409. Aici ar fi deosebit de urat: al doilea `create` pe
 * aceeasi editie **este** un 409, si fara aruncare ecranul ar desena rezumatul
 * unei cereri care n-a fost inregistrata.
 *
 * ⚠️ Timestamp-urile ajung **string-uri ISO**, desi tipurile generate spun `Date`
 * — `transformers.gen.ts` exista, dar nu ruleaza niciodata. De-aia modulul pur
 * `stare-recipiente.ts` lucreaza pe string, nu pe `Date`, si de-aia nu se cheama
 * `.getTime()` nicaieri.
 */
class WasteContainerRequestsAPI {
    /**
     * O cerere noua. Backendul refuza a doua cerere pe acelasi fel si aceeasi
     * editie cu **409** — plasa care ramane sub ecran, nu fluxul normal: starea
     * se citeste inainte de apasare, si felul deja cerut n-are buton.
     *
     * ⚠️ `create` are raspunsul declarat `{ [key: string]: unknown }` in spec, nu
     * DTO-ul — de-aia castul. Nu-l consuma nimeni azi (dupa succes se invalideaza
     * `own` si se reciteste adevarul de la server), si asa ramane: un cast nu e o
     * dovada despre ce a trimis serverul.
     */
    async create(dto: WasteContainerRequestDto): Promise<WasteContainerRequestDto> {
        const raspuns = await createWasteContainerRequest({ body: dto });
        return handleResponse(raspuns) as WasteContainerRequestDto;
    }

    /** Cererile scolii proprii — sursa starii de pe ecranul profesorului. */
    async getOwn(): Promise<WasteContainerRequestDto[]> {
        const raspuns = await getOwnWasteContainerRequests();
        return handleResponse(raspuns) ?? [];
    }

    /**
     * Raportul de administrare, PAGINAT (din 29.09): filtrul pe stare/fel, cautarea si
     * sortarea (lista alba: stare, scoala, codSe, judet, fel, numar, data) se fac pe server.
     * Fara `editionId`, backendul ia editia curenta.
     */
    async getReport(
        filter: FiltruRaportRecipiente,
        pageable: Pageable,
    ): Promise<Page<WasteContainerRequestReportDto>> {
        // `as any`, ca la celelalte rapoarte paginate (vezi `tickets.api.ts`): tipul GENERAT
        // declara `pageable` imbricat, dar Spring leaga `page`/`size`/`sort` PLATE.
        const raspuns = await getWasteContainerRequestsReport({ query: { ...filter, ...pageable } as any });
        return normalizePage(handleResponse(raspuns));
    }

    /**
     * Cifrele chip-urilor, pe TOATA editia (nu pe pagina si nici dupa cautare).
     * ⚠️ Spec-ul declara `bigint`, pe sarma sunt `number` — transformerele nu ruleaza.
     */
    async getContoare(editionId?: string): Promise<WasteContainerRequestContoareDto> {
        const raspuns = await getWasteContainerRequestsReportCounters({
            query: editionId ? { editionId } : undefined,
        });
        return handleResponse(raspuns);
    }

    /**
     * Marcheaza cererea drept preluata de un om.
     *
     * 🔴 Din b20 intoarce **randul intreg** (`WasteContainerRequestReportDto`), cu
     * `procesataLaDt` si `procesataDeNume` — nu doar `stare`. Deci tabelul de
     * administrare NU mai trebuie sa reincarce raportul dupa 200 ca sa afle data;
     * pana la b20 exact asta era restanta.
     */
    async markProcessed(id: string): Promise<WasteContainerRequestReportDto> {
        const raspuns = await markWasteContainerRequestProcessed({ path: { id } });
        return handleResponse(raspuns);
    }

    /**
     * Marcheaza cererea drept livrata (SE-18, PROCESATA -> LIVRATA). Poate si
     * LOGISTIC, nu doar DBEST/ADMIN — verificat pe sursa backend, echipa care
     * livreaza vede oricum lista pe `GET /report`.
     */
    async markDelivered(id: string): Promise<WasteContainerRequestReportDto> {
        const raspuns = await markWasteContainerRequestDelivered({ path: { id } });
        return handleResponse(raspuns);
    }

    /**
     * Corecteaza cantitatea, contactul sau adresa — doar cat cererea e NOUA
     * (SE-18, `WasteContainerRequestEditDto`, toate campurile optionale).
     * DBEST/ADMIN, nu LOGISTIC — backendul refuza cu 409 dupa ce cererea a fost
     * confirmata.
     */
    async edit(id: string, dto: WasteContainerRequestEditDto): Promise<WasteContainerRequestReportDto> {
        const raspuns = await editWasteContainerRequest({ path: { id }, body: dto });
        return handleResponse(raspuns);
    }
}

export const wasteContainerRequestsApi = new WasteContainerRequestsAPI();
